import { Injectable, inject } from '@angular/core';
import { Platform, AlertController, ToastController } from '@ionic/angular/standalone';
import {
  AppUpdate,
  AppUpdateAvailability,
  FlexibleUpdateInstallStatus,
} from '@capawesome/capacitor-app-update';

/**
 * AppUpdateService — Checks Google Play for app updates using the In-App Updates API.
 *
 * Supports two modes:
 * - Immediate: Full-screen blocking update (priority >= 4 in Play Console).
 * - Flexible: Background download with a toast to restart when ready.
 *
 * Should be called once on app startup from AppComponent.
 * Only runs on Android native — no-ops on web/iOS.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {
  private readonly platform = inject(Platform);
  private readonly alertCtrl = inject(AlertController);
  private readonly toastCtrl = inject(ToastController);

  /**
   * Check for available updates and trigger the appropriate update flow.
   * Safe to call on any platform — exits early if not on Android native.
   */
  async checkForUpdate(): Promise<void> {
    if (!this.platform.is('android') || this.platform.is('mobileweb')) {
      return;
    }

    try {
      const info = await AppUpdate.getAppUpdateInfo();

      if (info.updateAvailability !== AppUpdateAvailability.UPDATE_AVAILABLE) {
        return;
      }

      if (info.immediateUpdateAllowed) {
        await this.performImmediateUpdate();
      } else if (info.flexibleUpdateAllowed) {
        await this.performFlexibleUpdate();
      }
    } catch (error) {
      // Silently fail — don't block the technician if Play Services is unavailable
      console.warn('[AppUpdateService] Update check failed:', error);
    }
  }

  /**
   * Immediate update — shows full-screen Play Store UI.
   * The app restarts automatically after installation.
   *
   * If the technician dismisses the Play Store flow (it has a close button),
   * we re-launch it in a loop: they cannot keep using the app on the old
   * version. This is what makes the immediate update genuinely mandatory.
   */
  private async performImmediateUpdate(): Promise<void> {
    for (;;) {
      try {
        await AppUpdate.performImmediateUpdate();
        // On success Play restarts the app, so this point is effectively
        // unreachable — but return to be safe.
        return;
      } catch (error) {
        // User declined or the flow failed. Block them with a non-dismissible
        // alert whose only action re-triggers the update flow.
        console.warn('[AppUpdateService] Immediate update declined — re-prompting:', error);
        const retry = await this.showUpdateRequiredAlert();
        if (!retry) {
          // Play flow is no longer available (e.g. Play Services error);
          // fall back to opening the store listing and stop looping.
          await AppUpdate.openAppStore();
          return;
        }
      }
    }
  }

  /**
   * Flexible update — downloads in background, then prompts to restart.
   */
  private async performFlexibleUpdate(): Promise<void> {
    try {
      await AppUpdate.startFlexibleUpdate();
      await this.listenForFlexibleUpdateCompletion();
    } catch (error) {
      console.warn('[AppUpdateService] Flexible update failed:', error);
    }
  }

  /**
   * After a flexible update download completes, prompt the user to restart.
   */
  private async listenForFlexibleUpdateCompletion(): Promise<void> {
    await AppUpdate.addListener('onFlexibleUpdateStateChange', async (state) => {
      if (state.installStatus === FlexibleUpdateInstallStatus.DOWNLOADED) {
        const toast = await this.toastCtrl.create({
          message: 'Actualización lista. Reinicia para aplicar.',
          duration: 0, // persistent until action
          position: 'bottom',
          buttons: [
            {
              text: 'Reiniciar',
              role: 'confirm',
              handler: () => {
                void AppUpdate.completeFlexibleUpdate();
              },
            },
          ],
        });
        await toast.present();
      }
    });
  }

  /**
   * Non-dismissible alert shown when the immediate update is declined.
   * Resolves to `true` when the technician taps "Actualizar" (the caller
   * should then re-launch the update flow). The alert cannot be dismissed
   * by tapping the backdrop or the hardware back button.
   */
  private async showUpdateRequiredAlert(): Promise<boolean> {
    let resolveRetry!: (retry: boolean) => void;
    const retryPromise = new Promise<boolean>((resolve) => {
      resolveRetry = resolve;
    });

    const alert = await this.alertCtrl.create({
      header: 'Actualización Requerida',
      message:
        'Hay una nueva versión de la app que es necesaria para seguir operando. Debes actualizar desde Google Play para continuar.',
      backdropDismiss: false,
      buttons: [
        {
          text: 'Actualizar',
          handler: () => {
            resolveRetry(true);
          },
        },
      ],
    });
    await alert.present();

    return retryPromise;
  }
}
